<script setup>
import {computed} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useFieldsetsStore} from '@/stores/fieldsets'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useViewStore} from '@/stores/view'
import SearchFilterColumnMenu from './SearchFilterColumnMenu'
import SearchFilterColumnOptions from './SearchFilterColumnOptions'
import SearchFilterField from './SearchFilterField'
import SearchFilterItemResetBtn from "./SearchFilterItemResetBtn.vue"
import Button from '@/common/Button.vue'

const props = defineProps({
    viewId: String,
    filterKey: Number
})

// Dynamic View Store
const viewStore = useViewStore(props.viewId)

// Pinia Stores
const componentsStore = useComponentsStore()
const fieldsetsStore = useFieldsetsStore()
const componentStructureStore = useComponentStructureStore()

// Computed properties
const tableName = computed(() => {
    return viewStore.tableName || null
})

const componentId = computed(() => {
    if (!tableName.value) return null
    return componentsStore.componentIds?.[tableName.value] || null
})

const fields = computed(() => {
    if (!componentId.value) return null
    return componentStructureStore.get(componentId.value) || null
})

const firstFieldsetId = computed(() => {
    if (!componentId.value || !fieldsetsStore.items) return null

    // Safe fallback lookup to prevent reading properties of undefined
    const fieldset = fieldsetsStore.items.find(o => o.componentId === componentId.value)
    return fieldset ? fieldset.id : null
})

const columns = computed(() => {
    // Guard execution: If the required metadata is not ready, return null
    // This naturally delays child rendering via v-if="columns" safely
    if (firstFieldsetId.value === null || !fields.value) return null

    const idColumn = {
        fieldsetId: firstFieldsetId.value,
        columnName: 'id',
        title: 'ID',
        type: 'number'
    }

    const activeDataTypes = [
        'checkbox',
        'color',
        'currency',
        'date',
        'dateDate',
        'datetime-local',
        'email',
        'internalLink',
        'number',
        'password',
        'range',
        'relationshipOneToMany',
        'relationshipManyToMany',
        'select',
        'tel',
        'text',
        'textarea',
        'texteditor',
        'time',
        'url',
    ]

    const filteredColumns = fields.value.filter(o => {
        return activeDataTypes.indexOf(o.type) >= 0
    })

    return [idColumn].concat(filteredColumns)
})

// Methods
const deleteFilter = () => {
    viewStore.deleteFilter(props.filterKey)
}
</script>

<template>
    <div v-if="columns" class="root d-flex align-items-center py-3 border-bottom">
        <div class="flex-fill d-flex align-items-center">
            <SearchFilterColumnMenu :view-id="viewId" :filterKey="filterKey" :columns="columns"/>
            <SearchFilterColumnOptions :view-id="viewId" :filterKey="filterKey" :columns="columns" class="ms-1"/>
            <SearchFilterField :view-id="viewId" :filterKey="filterKey" class="ms-1 focus-ring"
                               data-search-filter-field/>
            <SearchFilterItemResetBtn :view-id="viewId" :filter-key="filterKey" class="ms-1"/>
        </div>

        <Button @click="deleteFilter" class="ms-1 btn-sm focus-ring"><i class="bi bi-trash3"/></Button>
    </div>
</template>

<style scoped>
.root > button {
    visibility: hidden;
}

.root:hover button {
    visibility: visible;
}
</style>